import { CommandError } from '../errors.js';
import { personOf } from './decisions.js';
import { isLevelKey, isTargetLevelKey } from '../fields.js';
import { required, } from './types.js';
/** Fields compared numerically when deciding whether an override equals the base record. */
const NUMERIC_FIELDS = new Set(['salary', 'fte']);
function headsFor(slice, perspective) {
    return required(slice, 'heads')[perspective] ?? {};
}
function overridesFor(slice, perspective) {
    return required(slice, 'overrides')[perspective] ?? {};
}
function renamesFor(slice, perspective) {
    return required(slice, 'groupRenames')[perspective] ?? {};
}
/** The last path segment's value, for a readable summary of a group id. */
function groupLabelOf(groupId) {
    if (!groupId.startsWith('G:'))
        return groupId;
    if (groupId === 'G:__org__')
        return 'the organisation';
    const last = groupId.slice(2).split('||').pop() ?? '';
    const eq = last.indexOf('=');
    return eq < 0 ? last : last.slice(eq + 1);
}
/* ------------------------------------------------------------------ *
 * Heads
 * ------------------------------------------------------------------ */
/**
 * The command that restores a head designation to exactly what it was, INCLUDING absence.
 *
 * `clearHead` and `setHead(null)` are different states, so the inverse has to pick the right one: if
 * there was no row, undo must remove the row, not write an explicit "no head".
 */
function restoreHead(scenarioId, perspective, groupId, heads) {
    return [
        Object.prototype.hasOwnProperty.call(heads, groupId)
            ? { type: 'setHead', scenarioId, perspective, groupId, employeeId: heads[groupId] ?? null }
            : { type: 'clearHead', scenarioId, perspective, groupId },
    ];
}
export const setHead = {
    needs: ['employees', 'buildUps', 'heads'],
    apply(slice, command) {
        const heads = headsFor(slice, command.perspective);
        // A planned hire can head a team — that is precisely why the original has to clear designations
        // when a build-up is deleted (removeHeadEverywhere) — so both populations are candidates.
        const name = command.employeeId === null ? null : personOf(slice, command.employeeId).name;
        const group = groupLabelOf(command.groupId);
        return {
            writes: [
                {
                    t: 'head',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    groupId: command.groupId,
                    employeeId: command.employeeId,
                    present: true,
                },
            ],
            inverse: restoreHead(command.scenarioId, command.perspective, command.groupId, heads),
            summary: name === null
                ? `${group} has no head [${command.perspective}]`
                : `${name} heads ${group} [${command.perspective}]`,
            touched: [`head:${command.perspective}:${command.groupId}`],
        };
    },
};
export const clearHead = {
    needs: ['heads'],
    apply(slice, command) {
        const heads = headsFor(slice, command.perspective);
        return {
            writes: [
                {
                    t: 'head',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    groupId: command.groupId,
                    employeeId: null,
                    present: false,
                },
            ],
            inverse: restoreHead(command.scenarioId, command.perspective, command.groupId, heads),
            summary: `Removed the head designation for ${groupLabelOf(command.groupId)} [${command.perspective}]`,
            touched: [`head:${command.perspective}:${command.groupId}`],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Group renames
 * ------------------------------------------------------------------ */
/**
 * Rewrite every group id whose path contains `levelKey=oldValue` to carry `newValue` instead.
 *
 * This is the part the original omits. Group ids embed resolved level values, so renaming a group
 * silently orphans anything keyed to the old id — heads first among them.
 */
function rekeyHeads(scenarioId, perspective, heads, levelKey, from, to) {
    const writes = [];
    let moved = 0;
    for (const [groupId, employeeId] of Object.entries(heads)) {
        if (!groupId.startsWith('G:'))
            continue;
        const segments = groupId.slice(2).split('||');
        let hit = false;
        const rewritten = segments.map((segment) => {
            const eq = segment.indexOf('=');
            if (eq < 0)
                return segment;
            if (segment.slice(0, eq) !== levelKey || segment.slice(eq + 1) !== from)
                return segment;
            hit = true;
            return `${levelKey}=${to}`;
        });
        if (!hit)
            continue;
        const newId = 'G:' + rewritten.join('||');
        if (newId === groupId)
            continue;
        // Order matters only in that both land in one transaction; the old key must not survive.
        writes.push({ t: 'head', scenarioId, perspective, groupId, employeeId: null, present: false });
        writes.push({ t: 'head', scenarioId, perspective, groupId: newId, employeeId, present: true });
        moved++;
    }
    return { writes, moved };
}
export const renameGroup = {
    needs: ['heads', 'groupRenames'],
    apply(slice, command) {
        if (command.perspective === 'as-is') {
            // The as-is view is the imported data. Relabelling a group there would be an edit to the
            // source, which is setEmpField's job — a scenario rename is explicitly non-destructive.
            throw new CommandError('invalid', 'A group rename belongs to a scenario, not to the as-is view');
        }
        const renames = renamesFor(slice, command.perspective);
        const forLevel = renames[command.levelKey] ?? {};
        const previous = Object.prototype.hasOwnProperty.call(forLevel, command.sourceValue)
            ? forLevel[command.sourceValue]
            : null;
        // What the group is displayed as now, and what it will be displayed as: the identity rewrite
        // moves from the former to the latter.
        const displayedBefore = previous ?? command.sourceValue;
        const displayedAfter = command.newValue ?? command.sourceValue;
        const writes = [
            {
                t: 'groupRename',
                scenarioId: command.scenarioId,
                perspective: command.perspective,
                fieldKey: command.levelKey,
                oldValue: command.sourceValue,
                newValue: command.newValue,
            },
        ];
        let moved = 0;
        if (displayedBefore !== displayedAfter) {
            const rekeyed = rekeyHeads(command.scenarioId, command.perspective, headsFor(slice, command.perspective), command.levelKey, displayedBefore, displayedAfter);
            writes.push(...rekeyed.writes);
            moved = rekeyed.moved;
        }
        return {
            writes,
            inverse: [
                {
                    type: 'renameGroup',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    levelKey: command.levelKey,
                    sourceValue: command.sourceValue,
                    newValue: previous,
                },
            ],
            summary: command.newValue === null
                ? `Restored the original name "${command.sourceValue}" [${command.perspective}]`
                : `Renamed "${displayedBefore}" → "${displayedAfter}" [${command.perspective}]` +
                    (moved ? ` (${moved} head designation${moved === 1 ? '' : 's'} moved with it)` : ''),
            touched: [`groupRename:${command.perspective}:${command.levelKey}:${command.sourceValue}`],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Field edits and group moves
 * ------------------------------------------------------------------ */
/**
 * One override write, with the original's auto-revert.
 *
 * A value equal to the base record removes the override instead of storing it, so "I moved this
 * person back" leaves no trace claiming they were moved. `baseValue` is undefined when the caller
 * cannot cheaply resolve it, in which case the value is always stored.
 */
/**
 * A scenario override, or its removal when the value has come back to the imported one.
 *
 * `baseValue` is not optional decoration: the app compares against the base on EVERY scenario write and
 * deletes the override when they match (orgchart.html:2372-2379), so a caller that omits it stores an
 * override the app would not have. It was omitted at every call site, which had two consequences —
 * setting a field back to its imported value left a redundant override behind, and, worse, an INVERSE
 * that meant "there was no override here" wrote an explicit null instead. `getEmpField` reads presence
 * with `hasOwn` (packages/core/src/fields.ts), so an explicit null is a real value: undoing a job-title
 * edit blanked the field rather than restoring what was imported.
 */
function overrideWrite(scenarioId, perspective, employeeId, fieldKey, value, baseValue) {
    const sameAsBase = baseValue !== undefined &&
        (NUMERIC_FIELDS.has(fieldKey)
            ? Number(value) === (Number(baseValue) || 0)
            : String(value ?? '') === String(baseValue ?? ''));
    return {
        t: 'override',
        scenarioId,
        perspective,
        employeeId,
        fieldKey,
        value: sameAsBase ? undefined : value,
    };
}
/**
 * The base record's current value for a field, in the shape an override value takes.
 *
 * This is what an as-is edit's inverse restores. A field the slice does not carry returns null rather
 * than undefined, because the inverse command's `value` is nullable and null there means "clear it" —
 * which is the correct restoration for a field that was empty.
 */
function baseFieldOf(person, fieldKey) {
    if (isLevelKey(fieldKey)) {
        const v = person.levels?.[fieldKey];
        return v === undefined || v === null ? null : v;
    }
    switch (fieldKey) {
        case 'name': return person.name;
        case 'salary': return person.salary;
        case 'fte': return person.fte;
        case 'dedication': return person.dedication;
        case 'openPosition': return person.openPosition ? 1 : 0;
        case 'inactive': return person.inactive ? 1 : 0;
        case 'supervisorId': return person.supervisorId ?? null;
        case 'jobTitle': return person.jobTitle ?? null;
        case 'location': return person.location ?? null;
        case 'country': return person.country ?? null;
        case 'seniority': return person.seniority ?? null;
        case 'businessUnit': return person.businessUnit ?? null;
        case 'legalEntity': return person.legalEntity ?? null;
        default: return null;
    }
}
/**
 * Turn an as-is field edit into a roster patch.
 *
 * The as-is record IS the imported data, so correcting it is a data correction and not a scenario
 * decision — which is why it cannot be an override: an override is invisible in the as-is view, exports
 * as a design choice, and would be destroyed the next time somebody reset that scenario's
 * restructuring. The fresh fact would evaporate.
 *
 * A hierarchy level lives in the row's `levels` map and everything else is a column, so which one this
 * is decides the shape of the patch. `id` is the row's identity and is refused rather than silently
 * ignored.
 */
function rosterPatch(employeeId, fieldKey, value) {
    if (fieldKey === 'id') {
        throw new CommandError('invalid', 'A personnel id identifies the record and cannot be edited');
    }
    if (isLevelKey(fieldKey)) {
        return {
            t: 'employeePatch',
            patches: [
                { employeeId, levels: { [fieldKey]: value === null ? null : String(value) } },
            ],
        };
    }
    switch (fieldKey) {
        // The three numerics: a blank clears the cell rather than storing zero, because zero salary and
        // unknown salary are different facts and the cost engines treat them differently.
        case 'salary':
        case 'fte':
        case 'dedication': {
            const n = value === null || value === '' ? null : Number(value);
            if (n !== null && !Number.isFinite(n)) {
                throw new CommandError('invalid', `${fieldKey} must be a number`);
            }
            return { t: 'employeePatch', patches: [{ employeeId, fields: { [fieldKey]: n } }] };
        }
        case 'openPosition':
        case 'inactive':
            return {
                t: 'employeePatch',
                patches: [{ employeeId, fields: { [fieldKey]: value !== null && value !== '' && value !== 0 } }],
            };
        case 'name': {
            // NOT NULL in the schema, and a nameless person renders as their job title everywhere.
            const name = value === null ? '' : String(value).trim();
            if (!name)
                throw new CommandError('invalid', 'A person must have a name');
            return { t: 'employeePatch', patches: [{ employeeId, fields: { name } }] };
        }
        case 'supervisorId':
        case 'jobTitle':
        case 'location':
        case 'country':
        case 'seniority':
        case 'businessUnit':
        case 'legalEntity':
            return {
                t: 'employeePatch',
                patches: [{ employeeId, fields: { [fieldKey]: value === null ? null : String(value) } }],
            };
        default:
            throw new CommandError('invalid', `"${fieldKey}" is not a field of the imported record; edit it within a scenario instead`);
    }
}
/**
 * A target placement is not a field edit, and must not be reachable as one.
 *
 * `setEmpField` accepts any non-reserved `fieldKey` and — through `headPerspectiveSchema` — the
 * `target` perspective, which is exactly where placements are stored. That let a caller write a raw
 * `tlvl2` row for somebody with no `tlvl1`: `placementPath` stops at the first gap, so the chart, the
 * rollup and every repath handler cannot see the value, while the workbook exporter writes it out as
 * `Target L2 = Sales`. An unreconcilable number, and a person who teleports into a unit they were
 * never placed in the moment somebody sets their first level.
 *
 * Not reachable from this client — `PersonPanel` is typed to the three real perspectives — but it is an
 * open door under the surface the Target phase ships, and the P6 tool definitions are generated from
 * these same schemas.
 */
function assertNotTargetPlacement(perspective, fieldKey) {
    if (perspective !== 'target' && !isTargetLevelKey(fieldKey))
        return;
    throw new CommandError('invalid', 'Target placements are set with setTargetPlacement, not by editing a field');
}
export const setEmpField = {
    needs: ['employees', 'buildUps', 'overrides'],
    apply(slice, command) {
        assertNotTargetPlacement(command.perspective, command.fieldKey);
        const person = personOf(slice, command.employeeId);
        /*
          A planned hire is edited on the position, in every perspective.
    
          The as-is refusal was always right — there is no imported record to change. But the scenario
          branch below happily wrote an override keyed by the hire's id, `getEmpField` honoured it, and the
          panel then displayed a salary that NOTHING pricing the hire reads: `hiringCostOf`, the Day 1/Day
          2 bridges and the exporter all take the value off the build-up row. So a user "corrected" a
          figure, watched it change on screen, and every number derived from it stayed on the old one.
          Refusing outright and sending them to `updateBuildUp` is the only answer that keeps the two
          agreeing.
        */
        if (person.isBuildUp) {
            throw new CommandError('invalid', 'A planned hire has no imported record — edit the position itself');
        }
        if (command.perspective === 'as-is') {
            const previous = baseFieldOf(person, command.fieldKey);
            return {
                writes: [rosterPatch(command.employeeId, command.fieldKey, command.value)],
                inverse: [
                    {
                        type: 'setEmpField',
                        scenarioId: command.scenarioId,
                        perspective: 'as-is',
                        employeeId: command.employeeId,
                        fieldKey: command.fieldKey,
                        value: previous,
                    },
                ],
                summary: `${person.name}: ${command.fieldKey} → ${command.value ?? '(cleared)'} [imported record]`,
                touched: [`employee:${command.employeeId}:${command.fieldKey}`],
            };
        }
        const existing = overridesFor(slice, command.perspective)[command.employeeId] ?? {};
        const had = Object.prototype.hasOwnProperty.call(existing, command.fieldKey);
        const base = baseFieldOf(person, command.fieldKey);
        /*
          With no override to restore, the way back is the IMPORTED value, not null.
          `overrideWrite` then sees it match the base and removes the row, which is what "there was no
          override here" means. Saying null instead wrote one that `getEmpField` honours.
        */
        const previous = had ? existing[command.fieldKey] : base;
        return {
            writes: [
                overrideWrite(command.scenarioId, command.perspective, command.employeeId, command.fieldKey, command.value, base),
            ],
            inverse: [
                {
                    type: 'setEmpField',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    employeeId: command.employeeId,
                    fieldKey: command.fieldKey,
                    value: previous,
                },
            ],
            summary: `${person.name}: ${command.fieldKey} → ${command.value ?? '(cleared)'} [${command.perspective}]`,
            touched: [`override:${command.perspective}:${command.employeeId}:${command.fieldKey}`],
        };
    },
};
export const setEmpFieldBulk = {
    needs: ['employees', 'buildUps', 'overrides'],
    apply(slice, command) {
        assertNotTargetPlacement(command.perspective, command.fieldKey);
        /*
          The planned-hire refusal, at the top, so it covers EVERY perspective — as `setEmpField`'s does.
    
          It used to live inside the as-is branch only. The scenario branch called `personOf` and threw the
          result away without looking at `isBuildUp`, so the same edit that was refused for one person was
          accepted for fifty. Verified by executing both handlers against the same slice: `setEmpField` on a
          hire refused in as-is AND remain; `setEmpFieldBulk` refused in as-is and ACCEPTED in remain,
          writing `{t:'override', perspective:'remain', employeeId:'bu1', fieldKey:'salary'}`.
    
          The consequence is the one the single command's own comment was written to close: `getEmpField`
          returns that override to the panel, while `hiringCostOf`, both stage bridges and the exporter all
          keep reading the build-up row. The user corrects a salary, watches it change on screen, and every
          number derived from it stays on the old one. A cross-chart multi-select is heterogeneous by
          construction and hires were deliberately made selectable, so this is the path that reaches it.
        */
        for (const assignment of command.assignments) {
            const person = personOf(slice, assignment.employeeId);
            if (person.isBuildUp) {
                throw new CommandError('invalid', `${person.name} is a planned hire and has no imported record — edit the position itself`);
            }
        }
        if (command.perspective === 'as-is') {
            // Same route as the single edit, batched into one descriptor. The inverse is a bulk edit carrying
            // each person's own previous value, which is why it can restore a mixed set in one step.
            const patches = [];
            const restore = [];
            for (const assignment of command.assignments) {
                const person = personOf(slice, assignment.employeeId);
                restore.push({ employeeId: assignment.employeeId, value: baseFieldOf(person, command.fieldKey) });
                patches.push(rosterPatch(assignment.employeeId, command.fieldKey, assignment.value));
            }
            return {
                writes: [
                    {
                        t: 'employeePatch',
                        patches: patches.flatMap((w) => (w.t === 'employeePatch' ? [...w.patches] : [])),
                    },
                ],
                inverse: [
                    {
                        type: 'setEmpFieldBulk',
                        scenarioId: command.scenarioId,
                        perspective: 'as-is',
                        fieldKey: command.fieldKey,
                        assignments: restore,
                    },
                ],
                summary: `${command.assignments.length} people: ${command.fieldKey} corrected on the imported record`,
                touched: command.assignments.map((a) => `employee:${a.employeeId}:${command.fieldKey}`),
            };
        }
        const byPerson = overridesFor(slice, command.perspective);
        const writes = [];
        const touched = [];
        const restore = [];
        for (const assignment of command.assignments) {
            const person = personOf(slice, assignment.employeeId);
            const existing = byPerson[assignment.employeeId] ?? {};
            const had = Object.prototype.hasOwnProperty.call(existing, command.fieldKey);
            const base = baseFieldOf(person, command.fieldKey);
            writes.push(overrideWrite(command.scenarioId, command.perspective, assignment.employeeId, command.fieldKey, assignment.value, base));
            touched.push(`override:${command.perspective}:${assignment.employeeId}:${command.fieldKey}`);
            // The imported value where there was no override, so the restore removes the row rather than
            // writing a null one. See overrideWrite.
            restore.push({
                employeeId: assignment.employeeId,
                value: had ? existing[command.fieldKey] : base,
            });
        }
        const n = command.assignments.length;
        return {
            writes,
            inverse: [
                {
                    type: 'setEmpFieldBulk',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    fieldKey: command.fieldKey,
                    assignments: restore,
                },
            ],
            summary: `${command.fieldKey} set for ${n} ${n === 1 ? 'person' : 'people'} [${command.perspective}]`,
            touched,
        };
    },
};
export const moveGroup = {
    needs: ['employees', 'buildUps', 'overrides'],
    apply(slice, command) {
        const byPerson = overridesFor(slice, command.perspective);
        const writes = [];
        const touched = [];
        const restore = [];
        for (const employeeId of command.employeeIds) {
            const person = personOf(slice, employeeId);
            const existing = byPerson[employeeId] ?? {};
            const had = Object.prototype.hasOwnProperty.call(existing, command.levelKey);
            const base = baseFieldOf(person, command.levelKey);
            // Through `overrideWrite` rather than by hand, so a move back to somebody's imported group removes
            // the override instead of restating it — the same rule every other override write follows.
            writes.push(overrideWrite(command.scenarioId, command.perspective, employeeId, command.levelKey, command.targetValue, base));
            touched.push(`override:${command.perspective}:${employeeId}:${command.levelKey}`);
            restore.push({ employeeId, value: had ? existing[command.levelKey] : base });
        }
        const n = command.employeeIds.length;
        return {
            writes,
            // Undoes as a field edit, because the people had different previous values and only their own
            // restores them. The audit trail keeps the move as a move; the inverse is what puts it back.
            inverse: [
                {
                    type: 'setEmpFieldBulk',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    fieldKey: command.levelKey,
                    assignments: restore,
                },
            ],
            summary: `Moved ${command.groupLabel} → ${command.targetValue}, ${n} ${n === 1 ? 'person' : 'people'} [${command.perspective}]`,
            touched,
        };
    },
};
export const setLevelDisabled = {
    needs: ['scenario'],
    apply(slice, command) {
        const scenario = required(slice, 'scenario');
        const before = !!scenario.disabledLevels[command.levelKey];
        const next = { ...scenario.disabledLevels };
        if (command.disabled)
            next[command.levelKey] = true;
        else
            delete next[command.levelKey];
        return {
            writes: [
                { t: 'scenarioPatch', scenarioId: command.scenarioId, patch: { disabledLevels: next } },
            ],
            inverse: [
                {
                    type: 'setLevelDisabled',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    levelKey: command.levelKey,
                    disabled: before,
                },
            ],
            summary: `${command.disabled ? 'Flattened' : 'Restored'} level ${command.levelKey}`,
            touched: [`level:${command.levelKey}`],
        };
    },
};
/**
 * Discard one perspective's restructuring within one scenario.
 *
 * Not reversible: the whole point is to throw away an accumulated set of overrides, and reconstructing
 * them would mean carrying every discarded value in the event. The audit trail records that it
 * happened and how much went, which is what the original does not do at all — it clears silently, with
 * no log entry, leaving a change log that still describes restructuring nobody can find.
 */
export const resetRestructuring = {
    needs: ['overrides', 'groupRenames', 'scenario'],
    apply(slice, command) {
        const overrides = overridesFor(slice, command.perspective);
        const renames = renamesFor(slice, command.perspective);
        const scenario = required(slice, 'scenario');
        const people = Object.keys(overrides).length;
        const renamed = Object.values(renames).reduce((n, m) => n + Object.keys(m).length, 0);
        const flattened = Object.keys(scenario.disabledLevels).length;
        if (!people && !renamed && !flattened) {
            throw new CommandError('conflict', 'There is no restructuring to reset in this perspective');
        }
        const parts = [
            people ? `${people} moved ${people === 1 ? 'person' : 'people'}` : '',
            renamed ? `${renamed} group rename${renamed === 1 ? '' : 's'}` : '',
            flattened ? `${flattened} flattened level${flattened === 1 ? '' : 's'}` : '',
        ].filter(Boolean);
        return {
            writes: [
                { t: 'clearOverrides', scenarioId: command.scenarioId, perspective: command.perspective },
                { t: 'clearGroupRenames', scenarioId: command.scenarioId, perspective: command.perspective },
                { t: 'scenarioPatch', scenarioId: command.scenarioId, patch: { disabledLevels: {} } },
            ],
            inverse: null,
            summary: `Reset restructuring for ${command.perspective}: discarded ${parts.join(', ')}`,
            touched: [`restructuring:${command.perspective}`],
        };
    },
};
//# sourceMappingURL=structure.js.map